import type { RfqProject } from '../types';
import {
  paidInstallmentIds,
  releasedInstallmentIds,
  type SupplierBill,
  type SupplierPaymentTermsSpec,
} from './supplierBills';
import { isPrePortalReleaseId, PRE_PORTAL_RELEASE_ID } from './timberRfqPrePortalVersion';

export function formatApprovedQuoteDate(value: Date | null | undefined): string {
  if (!value || Number.isNaN(value.getTime())) return '';
  return value.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function timberQuoteApprovedAt(
  project: Pick<RfqProject, 'approvedSubmissionApprovedAt'>,
  submissionId: string | undefined,
): Date | null {
  if (!submissionId) return null;
  const raw = project.approvedSubmissionApprovedAt?.[submissionId];
  return raw instanceof Date && !Number.isNaN(raw.getTime()) ? raw : null;
}

export function timberQuoteApproverInitials(
  project: Pick<RfqProject, 'approvedSubmissionApproverInitials'>,
  submissionId: string | undefined,
): string {
  if (!submissionId) return '';
  return project.approvedSubmissionApproverInitials?.[submissionId]?.trim() || '';
}

export function cncQuoteApprovedAt(
  project: Pick<RfqProject, 'approvedSupplierApprovedAtByRelease'>,
  releaseId: string | null,
  orgId: string | undefined,
): Date | null {
  if (!orgId) return null;
  const map = project.approvedSupplierApprovedAtByRelease ?? {};
  const keys = releaseId
    ? isPrePortalReleaseId(releaseId)
      ? [PRE_PORTAL_RELEASE_ID, releaseId]
      : [releaseId]
    : Object.keys(map);
  for (const key of keys) {
    const raw = map[key]?.[orgId];
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  }
  return null;
}

export function cncQuoteApproverInitials(
  project: Pick<RfqProject, 'approvedSupplierApproverInitialsByRelease'>,
  releaseId: string | null,
  orgId: string | undefined,
): string {
  if (!orgId) return '';
  const map = project.approvedSupplierApproverInitialsByRelease ?? {};
  const keys = releaseId
    ? isPrePortalReleaseId(releaseId)
      ? [PRE_PORTAL_RELEASE_ID, releaseId]
      : [releaseId]
    : Object.keys(map);
  for (const key of keys) {
    const initials = map[key]?.[orgId]?.trim();
    if (initials) return initials;
  }
  return '';
}

export function findAwardBillForSubmission(
  bills: SupplierBill[],
  rfqProjectId: string | undefined,
  submissionId: string | undefined,
): SupplierBill | undefined {
  if (!rfqProjectId || !submissionId) return undefined;
  return bills.find(
    bill =>
      bill.status !== 'void'
      && bill.rfqProjectId === rfqProjectId
      && bill.rfqSubmissionId === submissionId,
  );
}

/**
 * Supplier-level CNC award bill — only `org:{supplier}` keys.
 * Never returns another quote's submission-keyed bill.
 */
/** Any non-void CNC bill for this RFQ + supplier org (any rfqSubmissionId key). */
export function findAwardBillForCncOrg(
  bills: SupplierBill[],
  rfqProjectId: string | undefined,
  orgId: string | undefined,
): SupplierBill | undefined {
  if (!rfqProjectId || !orgId) return undefined;
  const matches = bills.filter(
    bill =>
      bill.status !== 'void'
      && bill.category === 'cnc'
      && bill.rfqProjectId === rfqProjectId
      && bill.supplierOrgId === orgId,
  );
  if (!matches.length) return undefined;
  return matches.slice().sort((a, b) => {
    const aTerms = a.paymentTerms?.installments?.length ?? 0;
    const bTerms = b.paymentTerms?.installments?.length ?? 0;
    if (aTerms !== bTerms) return bTerms - aTerms;
    const aT = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
    const bT = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
    return bT - aT;
  })[0];
}

/** Date the first installment (or the bill) was marked paid on Billing & Payables. */
export function supplierBillPaidAt(bill: SupplierBill | null | undefined): Date | null {
  if (!bill) return null;
  const dates: Date[] = [];
  for (const payment of Object.values(bill.installmentPayments ?? {})) {
    if (payment.paidAt instanceof Date) dates.push(payment.paidAt);
  }
  for (const event of bill.statusHistory ?? []) {
    if (event.status === 'paid' && event.at instanceof Date) dates.push(event.at);
  }
  if (!dates.length) return null;
  return new Date(Math.min(...dates.map(d => d.getTime())));
}

export type ApprovedQuoteBannerMeta = {
  approvedAt: Date | null;
  initials: string;
  paidAt: Date | null;
  paymentHref: string | null;
};

export function billingPayablesFocusHref(opts: {
  dealId?: string | null;
  billId?: string | null;
}): string | null {
  const dealId = opts.dealId?.trim() || '';
  const billId = opts.billId?.trim() || '';
  if (!dealId && !billId) return null;
  const params = new URLSearchParams();
  if (dealId) params.set('deal', dealId);
  if (billId) params.set('bill', billId);
  return `/billing-payables?${params.toString()}`;
}

export type ApprovedQuotePaymentTermsTarget = {
  submissionKey: string;
  orgId?: string;
  vendorName: string;
  quoteLabel: string;
  amount: number;
  initialTerms?: SupplierPaymentTermsSpec | null;
  /** Installments already approved or paid on the AP bill. Their dollars stay fixed. */
  lockedInstallmentIds?: string[];
  /** Installments already marked paid. */
  paidInstallmentIds?: string[];
};

/** Opens payment-terms editor with bill breakdown when the AP bill already exists. */
export function buildApprovedQuotePaymentTermsTarget(args: {
  submissionKey: string;
  orgId?: string;
  vendorName: string;
  quoteLabel: string;
  quoteAmount: number | null | undefined;
  bill?: SupplierBill | null;
}): ApprovedQuotePaymentTermsTarget {
  return {
    submissionKey: args.submissionKey,
    orgId: args.orgId,
    vendorName: args.vendorName,
    quoteLabel: args.quoteLabel,
    amount: args.quoteAmount ?? args.bill?.amount ?? 0,
    initialTerms: args.bill?.paymentTerms ?? null,
    lockedInstallmentIds: args.bill ? releasedInstallmentIds(args.bill) : [],
    paidInstallmentIds: args.bill ? paidInstallmentIds(args.bill) : [],
  };
}

export function resolveApprovedQuoteBannerMeta(args: {
  approvedAt?: Date | null;
  initials?: string;
  bill?: SupplierBill | null;
  dealId?: string | null;
}): ApprovedQuoteBannerMeta {
  const stored = args.approvedAt instanceof Date ? args.approvedAt : null;
  const fallback = args.bill?.createdAt instanceof Date ? args.bill.createdAt : null;
  return {
    approvedAt: stored ?? fallback,
    initials: (args.initials || '').trim(),
    paidAt: supplierBillPaidAt(args.bill),
    paymentHref: billingPayablesFocusHref({
      dealId: args.dealId || args.bill?.dealId,
      billId: args.bill?.id,
    }),
  };
}
